import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Loader2, CheckCircle2, XCircle, Search, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const TABS = [
  { key: "pending", label: "In behandeling" },
  { key: "approved", label: "Goedgekeurd" },
  { key: "rejected", label: "Afgewezen" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};

const AdminMakelaarApprovals = () => {
  useSeo({ title: "Makelaar-goedkeuringen · Admin" });
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setData(null);
    const params = new URLSearchParams({ status });
    if (search.trim()) params.set("q", search.trim());
    api(`/admin/makelaar-approvals?${params.toString()}`).then(setData).catch((e) => toast.error(e.message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, search]);

  const decide = async (uid, action) => {
    const note = action === "reject" ? (window.prompt("Reden van afwijzing (optioneel):", "") ?? "") : "";
    setBusy(uid);
    try {
      await api(`/admin/makelaar-approvals/${uid}/${action}`, {
        method: "POST",
        body: JSON.stringify({ note }),
      });
      toast.success(action === "approve" ? "Goedgekeurd" : "Afgewezen");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const items = data?.items || [];
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0 };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy">
        <ArrowLeft size={14} /> Admin
      </Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="approvals-title">Makelaar-goedkeuringen</h1>
          <p className="mt-1 text-sm text-slate-500">
            Controleer KvK + lidmaatschap voordat een makelaar woningen kan plaatsen.
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-xl"><RefreshCw size={16} className="mr-2" /> Ververs</Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatus(t.key)}
              data-testid={`tab-${t.key}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                status === t.key ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t.label} <span className="ml-1 opacity-70">({counts[t.key] || 0})</span>
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(q); }} className="relative ml-auto w-full sm:w-80">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek naam, e-mail, kantoor, KvK…" className="rounded-xl pl-9" data-testid="search-input" />
        </form>
      </div>

      <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-sm">
        {!data ? (
          <div className="flex items-center justify-center p-16"><Loader2 className="animate-spin text-brandblue" size={28} /></div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-500" data-testid="empty-state">
            <Clock size={28} className="mx-auto mb-3 text-slate-300" />
            Geen makelaars{status === "pending" ? " in behandeling" : status === "approved" ? " goedgekeurd" : " afgewezen"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Makelaar</th>
                  <th className="px-5 py-3">Kantoor</th>
                  <th className="px-5 py-3">KvK</th>
                  <th className="px-5 py-3">NVM/VBO</th>
                  <th className="px-5 py-3">Aangemaakt</th>
                  <th className="px-5 py-3 text-right">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((u) => (
                  <tr key={u.id} data-testid={`approval-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-navy">{u.name || "—"}</div>
                      <div className="text-xs text-slate-400">{u.email}</div>
                    </td>
                    <td className="px-5 py-3 text-navy">{u.agency_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-5 py-3 font-mono text-slate-700">{u.kvk_number || <span className="text-slate-400">—</span>}</td>
                    <td className="px-5 py-3 font-mono text-slate-700">{u.nvm_vbo_number || <span className="text-slate-400">—</span>}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-500">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {status !== "approved" && (
                          <button
                            type="button"
                            disabled={busy === u.id}
                            onClick={() => decide(u.id, "approve")}
                            className="inline-flex items-center gap-1 rounded-lg bg-success/15 px-2.5 py-1.5 text-xs font-semibold text-success hover:bg-success/25 disabled:opacity-50"
                            data-testid={`approve-${u.id}`}
                          >
                            {busy === u.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Goedkeuren
                          </button>
                        )}
                        {status !== "rejected" && (
                          <button
                            type="button"
                            disabled={busy === u.id}
                            onClick={() => decide(u.id, "reject")}
                            className="inline-flex items-center gap-1 rounded-lg bg-danger/10 px-2.5 py-1.5 text-xs font-semibold text-danger hover:bg-danger/20 disabled:opacity-50"
                            data-testid={`reject-${u.id}`}
                          >
                            <XCircle size={13} /> Afwijzen
                          </button>
                        )}
                        <Link to={`/admin/users/${u.id}`} className="rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                          Profiel
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMakelaarApprovals;
